import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP, getTodayChallengeGame, completeDailyChallenge } from '../utils/xp';
import { speakText } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadGamePool, pickGameWords } from '../data/gameWords';
import './WordSearch.css';

const capitalize = (l) => l.charAt(0).toUpperCase() + l.slice(1);
const normalizeWord = (w) => w.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z]/g, '');

const GRID_SIZE = 10;
const DIRECTIONS = [
    [0, 1], [1, 0], [1, 1], [1, -1],
    [0, -1], [-1, 0], [-1, -1], [-1, 1],
];

const TEXT = {
    title: { en: 'Word Search', it: 'Cerca parole', es: 'Sopa de letras', fr: 'Mots mêlés', pt: 'Caça-palavras' },
    notEnough: { en: 'Not enough words yet', it: 'Non ci sono ancora abbastanza parole', es: 'Aún no hay suficientes palabras', fr: 'Pas encore assez de mots', pt: 'Ainda não há palavras suficientes' },
    notEnoughHint: {
        en: 'Save a few words to your Word Bank and this game will use them.',
        it: 'Salva qualche parola nel tuo Word Bank e il gioco le userà.',
        es: 'Guarda algunas palabras en tu Word Bank y el juego las usará.',
        fr: 'Enregistrez quelques mots dans votre Word Bank et le jeu les utilisera.',
        pt: 'Salve algumas palavras no seu Word Bank e o jogo vai usá-las.',
    },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
    building: { en: 'Building your puzzle...', it: 'Sto preparando il gioco...', es: 'Preparando tu juego...', fr: 'Préparation de votre grille...', pt: 'Preparando o seu jogo...' },
    done: { en: 'Puzzle complete!', it: 'Gioco completato!', es: '¡Juego completado!', fr: 'Grille terminée !', pt: 'Jogo concluído!' },
    time: { en: 'Time', it: 'Tempo', es: 'Tiempo', fr: 'Temps', pt: 'Tempo' },
    wordsFound: { en: 'words found', it: 'parole trovate', es: 'palabras encontradas', fr: 'mots trouvés', pt: 'palavras encontradas' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    hear: { en: 'Hear it', it: 'Ascolta', es: 'Escuchar', fr: 'Écouter', pt: 'Ouvir' },
    saved: { en: 'Saved to your Word Bank!', it: 'Salvata nel tuo Word Bank!', es: '¡Guardada en tu Word Bank!', fr: 'Enregistré dans votre Word Bank !', pt: 'Salva no seu Word Bank!' },
    save: { en: 'Save to Word Bank', it: 'Salva nel Word Bank', es: 'Guardar en Word Bank', fr: 'Enregistrer dans Word Bank', pt: 'Salvar no Word Bank' },
    close: { en: 'Close', it: 'Chiudi', es: 'Cerrar', fr: 'Fermer', pt: 'Fechar' },
};

// Single words that fit the grid (3 to 10 letters once accents are removed)
const fitsGrid = (item) => {
    if (!item.word || /\s/.test(item.word.trim())) return false;
    const n = normalizeWord(item.word).length;
    return n >= 3 && n <= GRID_SIZE;
};

// Chooses the words for one puzzle: the learner's own words first, the built-in list as support
function chooseWords(gamePool) {
    const picked = pickGameWords(gamePool, 12, fitsGrid);
    const seen = new Set();
    const words = [];
    picked.forEach(item => {
        const key = normalizeWord(item.word);
        if (seen.has(key)) return;
        seen.add(key);
        words.push(item.word.trim());
    });
    return words.slice(0, 10);
}

function createEmptyGrid() {
    return Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(''));
}

function canPlace(grid, word, row, col, dr, dc) {
    for (let i = 0; i < word.length; i++) {
        const r = row + dr * i;
        const c = col + dc * i;
        if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) return false;
        if (grid[r][c] !== '' && grid[r][c] !== word[i]) return false;
    }
    return true;
}

function placeWord(grid, word, row, col, dr, dc) {
    const newGrid = grid.map(r => [...r]);
    for (let i = 0; i < word.length; i++) {
        newGrid[row + dr * i][col + dc * i] = word[i];
    }
    return newGrid;
}

function buildGrid(words) {
    let grid = createEmptyGrid();
    const placed = [];

    for (const word of words) {
        const clean = normalizeWord(word);
        if (clean.length > GRID_SIZE || clean.length < 3) continue;
        let success = false;
        for (let attempt = 0; attempt < 100; attempt++) {
            const dr_dc = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
            const row = Math.floor(Math.random() * GRID_SIZE);
            const col = Math.floor(Math.random() * GRID_SIZE);
            if (canPlace(grid, clean, row, col, dr_dc[0], dr_dc[1])) {
                grid = placeWord(grid, clean, row, col, dr_dc[0], dr_dc[1]);
                placed.push({ word: clean, original: word, row, col, dr: dr_dc[0], dc: dr_dc[1] });
                success = true;
                break;
            }
        }
        if (!success) continue;
    }

    const letters = 'abcdefghijklmnopqrstuvwxyz';
    for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
            if (grid[r][c] === '') {
                grid[r][c] = letters[Math.floor(Math.random() * letters.length)];
            }
        }
    }

    return { grid, placed };
}

function getCells(start, end, grid) {
    if (!start || !end) return [];
    const cells = [];
    const dr = Math.sign(end.row - start.row);
    const dc = Math.sign(end.col - start.col);
    const rowDiff = Math.abs(end.row - start.row);
    const colDiff = Math.abs(end.col - start.col);
    if (rowDiff !== 0 && colDiff !== 0 && rowDiff !== colDiff) return [];
    const steps = Math.max(rowDiff, colDiff);
    for (let i = 0; i <= steps; i++) {
        cells.push({ row: start.row + dr * i, col: start.col + dc * i });
    }
    return cells;
}

function cellKey(r, c) { return `${r}-${c}`; }

export default function WordSearch() {
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const targetLangName = capitalize(targetLang);
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const navigate = useNavigate();
    const [grid, setGrid] = useState([]);
    const [placedWords, setPlacedWords] = useState([]);
    const [foundWords, setFoundWords] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [startCell, setStartCell] = useState(null);
    const [currentCell, setCurrentCell] = useState(null);
    const [foundCells, setFoundCells] = useState(new Set());
    const [wrongFlash, setWrongFlash] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [finished, setFinished] = useState(false);
    const [gamePool, setGamePool] = useState(null);
    const [saveChip, setSaveChip] = useState(null);
    const [chipSaved, setChipSaved] = useState(false);
    const [notEnough, setNotEnough] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            setNotEnough(false);
            const { data: { user } } = await supabase.auth.getUser();
            const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
            const loaded = await loadGamePool(user ? user.id : null, targetLang, interfaceLanguage, level);
            setGamePool(loaded);
            const words = chooseWords(loaded);
            if (words.length < 4) {
                setNotEnough(true);
                return;
            }
            const { grid: g, placed } = buildGrid(words);
            setGrid(g);
            setPlacedWords(placed);
        };
        init();
    }, [targetLang, activeLevel, interfaceLanguage]);

    useEffect(() => {
        if (grid.length > 0 && !finished) {
            timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [grid, finished]);

    useEffect(() => {
        if (placedWords.length > 0 && foundWords.length === placedWords.length) {
            setFinished(true);
            clearInterval(timerRef.current);
            awardXP(30, 'word-search');
            if (getTodayChallengeGame() === 'word-search') {
                completeDailyChallenge('word-search');
            }
        }
    }, [foundWords, placedWords]);

    const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    const getSelectedCells = () => {
        if (!selecting || !startCell || !currentCell) return new Set();
        return new Set(getCells(startCell, currentCell, grid).map(c => cellKey(c.row, c.col)));
    };

    const handleCellDown = (r, c) => {
        setSelecting(true);
        setStartCell({ row: r, col: c });
        setCurrentCell({ row: r, col: c });
    };

    const handleCellEnter = (r, c) => {
        if (!selecting) return;
        setCurrentCell({ row: r, col: c });
    };

    const handleCellUp = () => {
        if (!selecting || !startCell || !currentCell) { setSelecting(false); return; }
        const cells = getCells(startCell, currentCell, grid);
        const selected = cells.map(c => grid[c.row][c.col]).join('');
        const selectedReverse = cells.map(c => grid[c.row][c.col]).reverse().join('');

        const match = placedWords.find(pw =>
            !foundWords.includes(pw.word) &&
            (pw.word === selected || pw.word === selectedReverse)
        );

        if (match) {
            setFoundWords(prev => [...prev, match.word]);
            setFoundCells(prev => {
                const next = new Set(prev);
                cells.forEach(c => next.add(cellKey(c.row, c.col)));
                return next;
            });
        } else {
            setWrongFlash(true);
            setTimeout(() => setWrongFlash(false), 400);
        }

        setSelecting(false);
        setStartCell(null);
        setCurrentCell(null);
    };

    const selectedCells = getSelectedCells();

    const getCellStyle = (r, c) => {
        const key = cellKey(r, c);
        if (foundCells.has(key)) return 'ws-cell found';
        if (selectedCells.has(key)) return wrongFlash ? 'ws-cell wrong' : 'ws-cell selected';
        return 'ws-cell';
    };

    if (notEnough) {
        return (
            <div className="ws-screen">
                <div className="ws-top-bar">
                    <button className="ws-back" onClick={() => navigate('/games')}>←</button>
                    <div className="ws-title">{tx('title')}</div>
                    <div style={{ width: 32 }}></div>
                </div>
                <div className="ws-complete">
                    <div className="ws-complete-icon">🔤</div>
                    <div className="ws-complete-title">{tx('notEnough')}</div>
                    <div className="ws-complete-words">{tx('notEnoughHint')}</div>
                    <button className="ws-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    if (grid.length === 0) {
        return (
            <div className="ws-screen">
                <div className="ws-loading">{tx('building')}</div>
            </div>
        );
    }

    return (
        <div className="ws-screen">
            <div className="ws-top-bar">
                <button className="ws-back" onClick={() => navigate('/games')}>←</button>
                <div className="ws-title">{tx('title')}</div>
                <div className="ws-timer">{formatTime(seconds)}</div>
            </div>

            {finished ? (
                <div className="ws-complete">
                    <div className="ws-complete-icon">🎉</div>
                    <div className="ws-complete-title">{tx('done')}</div>
                    <div className="ws-complete-time">{tx('time')}: {formatTime(seconds)}</div>
                    <div className="ws-complete-words">{placedWords.length} {tx('wordsFound')}</div>
                    <div className="ws-complete-xp">+30 XP</div>
                    <button className="ws-play-again" onClick={() => { setFoundWords([]); setFoundCells(new Set()); setSeconds(0); setFinished(false); const { grid: g, placed } = buildGrid(chooseWords(gamePool)); setGrid(g); setPlacedWords(placed); }}>
                        {tx('again')}
                    </button>
                    <button className="ws-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            ) : (
                <>
                    <div className="ws-word-list">
                        {placedWords.map(pw => (
                            <div
                                key={pw.word}
                                className={`ws-word-chip ${foundWords.includes(pw.word) ? 'found' : ''}`}
                                onClick={() => { setSaveChip(pw); setChipSaved(false); }}
                            >
                                {foundWords.includes(pw.word) ? '✓ ' : ''}{pw.original || pw.word}
                            </div>
                        ))}
                    </div>

                    <div
                        className="ws-grid"
                        onMouseLeave={handleCellUp}
                        onTouchEnd={handleCellUp}
                    >
                        {grid.map((row, r) => (
                            <div key={r} className="ws-row">
                                {row.map((letter, c) => (
                                    <div
                                        key={c}
                                        className={getCellStyle(r, c)}
                                        onMouseDown={() => handleCellDown(r, c)}
                                        onMouseEnter={() => handleCellEnter(r, c)}
                                        onMouseUp={handleCellUp}
                                        onTouchStart={(e) => { e.preventDefault(); handleCellDown(r, c); }}
                                        onTouchMove={(e) => { e.preventDefault(); const touch = e.touches[0]; const el = document.elementFromPoint(touch.clientX, touch.clientY); if (el && el.dataset.row) handleCellEnter(parseInt(el.dataset.row), parseInt(el.dataset.col)); }}
                                        data-row={r}
                                        data-col={c}
                                    >
                                        {letter.toUpperCase()}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div className="ws-progress">
                        {foundWords.length} / {placedWords.length} {tx('wordsFound')}
                    </div>

                    {saveChip && (
                        <div className="ws-save-overlay" onClick={() => setSaveChip(null)}>
                            <div className="ws-save-sheet" onClick={e => e.stopPropagation()}>
                                <div className="ws-save-word">{saveChip.original || saveChip.word}</div>
                                <button className="ws-save-hear" onClick={() => speakText(saveChip.original || saveChip.word, undefined, targetLang)}>
                                    🔊 {tx('hear')}
                                </button>
                                {chipSaved ? (
                                    <div className="ws-save-done">✓ {tx('saved')}</div>
                                ) : (
                                    <button
                                        className="ws-save-btn"
                                        onClick={async () => {
                                            const { data: { user } } = await supabase.auth.getUser();
                                            if (!user) return;
                                            const wordToSave = saveChip.original || saveChip.word;

                                            await supabase.from('word_bank').upsert({
                                                user_id: user.id,
                                                word: wordToSave,
                                                source: 'manual',
                                                entry_type: 'word',
                                                target_language: targetLang,
                                            }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });

                                            setChipSaved(true);

                                            fetch('/api/identify-word', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ word: wordToSave, language: targetLangName, interfaceLanguage }),
                                            })
                                                .then(r => r.json())
                                                .then(async result => {
                                                    if (!result.valid) return;
                                                    const finalWord = result.wordToSave || wordToSave;
                                                    let note = result.note || '';
                                                    if (result.isConjugated) {
                                                        note = `You encountered "${result.typedWord}" — this is the ${result.conjugationInfo} form of this verb. ${note}`.trim();
                                                    }
                                                    await supabase.from('word_bank').upsert({
                                                        user_id: user.id,
                                                        word: finalWord,
                                                        translation: result.translation,
                                                        phonetic: result.phonetic,
                                                        type: result.type,
                                                        note,
                                                        examples: result.examples || [],
                                                        entry_type: finalWord.trim().split(/\s+/).length > 1 ? 'phrase' : 'word',
                                                        source: 'manual',
                                                        target_language: targetLang,
                                                    }, { onConflict: 'user_id,word,target_language' });
                                                })
                                                .catch(() => { });
                                        }}
                                    >
                                        🔖 {tx('save')}
                                    </button>
                                )}
                                <button className="ws-save-cancel" onClick={() => setSaveChip(null)}>
                                    {tx('close')}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}